import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { INDUSTRIES } from "./siteData";

const EASE = [0.22, 1, 0.36, 1];
const TINT = ["#f6e3df", "#e3ebe1", "#e2e9f1", "#f6ecd2", "#efe4f1", "#f7e4da", "#e4eeee", "#f1e7dc"];
const THR = ["#b5543a", "#3f7a5a", "#2f5b8a", "#c98f2e", "#8a4f9a", "#c0574a", "#2f7a7a", "#8a5a32"];
const SIZE = [210, 190, 220, 200, 195, 215, 200, 205];
const OFF = [0, 40, 10, 55, 30, 0, 45, 15];
const ICON = [
  "M70 58 L88 50 Q100 60 112 50 L130 58 L142 78 L128 86 L128 132 L72 132 L72 86 L58 78 Z",
  "M58 132 V92 L80 104 V92 L102 104 V72 H120 V60 H134 V132 Z M70 132 V118 H82 V132",
  "M60 80 L68 58 H132 L140 80 Q130 90 120 80 Q110 90 100 80 Q90 90 80 80 Q70 90 60 80 Z M66 86 V132 H134 V86 M90 132 V108 H110 V132",
  "M100 56 L138 74 V116 L100 134 L62 116 V74 Z M62 74 L100 92 L138 74 M100 92 V134",
  "M64 136 L86 72 L128 114 Z M100 60 V48 M118 66 L126 58 M126 84 H138 M78 92 L106 120",
  "M100 132 C 50 100, 58 60, 82 60 C 92 60, 98 68, 100 74 C 102 68, 108 60, 118 60 C 142 60, 150 100, 100 132 Z",
  "M58 76 H80 L88 64 H112 L120 76 H142 V128 H58 Z M100 116 A16 16 0 1 0 100.1 116",
  "M58 90 V110 H72 L120 132 V68 L72 90 Z M72 110 L80 134 H90 L86 112 M132 84 Q142 100 132 116",
];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Pinyon+Script&family=Caveat:wght@400;600&display=swap');
@keyframes ih-sway{0%,100%{transform:rotate(-2deg)}50%{transform:rotate(2deg)}}
@keyframes ih-stitch{to{stroke-dashoffset:-24}}
@keyframes ih-shine{0%{background-position:150% 0}100%{background-position:-50% 0}}
.ih-hand{font-family:'Caveat',cursive}
.ih-shine{display:inline-block;padding:0 .15em;background:linear-gradient(110deg,#8a5a32 35%,#f3d6a4 50%,#8a5a32 65%);background-size:250% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;animation:ih-shine 3.5s linear infinite}
.ih-cell{padding-top:0}
@media (min-width:768px){.ih-cell{padding-top:var(--off)}}
.ih-hang{transform-origin:50% 0;animation:ih-sway 5s ease-in-out infinite}
.ih-hang:hover{animation-play-state:paused}
.ih-hoop{position:relative;width:min(var(--s),40vw);height:min(var(--s),40vw);border-radius:50%;padding:11px;background:conic-gradient(from 20deg,#d9b88c,#b88d5e,#e3c79e,#a87a4c,#d9b88c);box-shadow:0 16px 30px rgba(43,29,22,.18),inset 0 0 0 1px rgba(90,60,30,.3);transition:transform .6s cubic-bezier(.2,.8,.2,1)}
.ih-hang:hover .ih-hoop{transform:scale(1.06) rotate(-6deg)}
.ih-clamp{position:absolute;left:50%;top:-12px;width:22px;height:20px;margin-left:-11px;border-radius:4px;background:linear-gradient(#e3c79e,#a87a4c);box-shadow:0 2px 4px rgba(0,0,0,.2)}
.ih-clamp::after{content:"";position:absolute;left:50%;top:-8px;width:8px;height:10px;margin-left:-4px;border-radius:3px;background:#c9a27a}
.ih-cloth{position:relative;width:100%;height:100%;border-radius:50%;overflow:hidden;display:flex;flex-direction:column;align-items:center;justify-content:center;background-image:repeating-linear-gradient(0deg,rgba(0,0,0,.035) 0 1px,transparent 1px 4px),repeating-linear-gradient(90deg,rgba(0,0,0,.035) 0 1px,transparent 1px 4px);box-shadow:inset 0 6px 16px rgba(0,0,0,.12)}
.ih-cloth::after{content:"";position:absolute;inset:8px;border-radius:50%;border:1.5px dashed rgba(138,90,50,.3);pointer-events:none}
.ih-cloth svg{width:46%;height:46%;margin:-8px 0 6px}
.ih-cloth svg path{fill:none;stroke:currentColor;stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:7 5}
.ih-hang:hover .ih-cloth svg path{animation:ih-stitch .6s linear infinite}
@media (prefers-reduced-motion:reduce){.ih-hang,.ih-shine,.ih-cloth svg path{animation:none}}
`;

export default function IndustryHoops() {
  return (
    <section className="relative overflow-hidden bg-[#FAF9F6] py-16 lg:py-20" data-testid="industry-hoops">
      <style>{CSS}</style>
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <motion.span initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="inline-flex items-center gap-4 text-[0.7rem] uppercase tracking-[0.32em] text-[#8a5a32]">
              <span className="h-px w-12 bg-[#8a5a32]" />Industries We Serve
            </motion.span>
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.15, ease: EASE }}
              className="mt-6 font-display text-[clamp(2.2rem,4.6vw,4rem)] font-light leading-[1.08] text-[#1A1A1A]">
              Trusted across textile &amp;<br />creative{" "}
              <span className="ih-shine" style={{ fontFamily: "'Pinyon Script', cursive", fontSize: "1.3em" }}>industries.</span>
            </motion.h2>
          </div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.6 }}
            className="ih-hand flex -rotate-3 items-center gap-2 text-3xl text-[#8a5a32] lg:mb-3">
            hand-stitched for every craft
            <svg width="60" height="40" viewBox="0 0 60 40" aria-hidden="true">
              <path d="M2 6 C 20 2, 40 10, 50 30 M50 30 l-10 -2 M50 30 l2 -10" fill="none" stroke="#8a5a32" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </motion.div>
        </div>

        <div className="relative mt-10 grid grid-cols-2 gap-y-8 md:grid-cols-4 md:gap-y-5">
          <div className="pointer-events-none absolute -left-5 -right-5 top-0 hidden border-t-2 border-dashed border-[#8a5a32]/30 md:block" />
          {INDUSTRIES.slice(0, 8).map((it, i) => (
            <motion.div key={it.name} className="ih-cell flex justify-center" style={{ "--off": `${OFF[i]}px` }}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 + i * 0.08, ease: EASE }}>
              <div className="ih-hang flex cursor-pointer flex-col items-center" style={{ animationDelay: `-${i * 0.6}s` }}>
                <span className="h-2.5 w-2.5 rounded-full shadow" style={{ background: "radial-gradient(circle at 35% 30%,#f1d7ae,#8a6a4a)" }} />
                <span className="w-px bg-[#a98a66]" style={{ height: 40 + (OFF[i] % 20) }} />
                <div className="ih-hoop" style={{ "--s": `${SIZE[i]}px` }}>
                  <span className="ih-clamp" />
                  <div className="ih-cloth" style={{ backgroundColor: TINT[i] }}>
                    <svg viewBox="45 40 110 110" style={{ color: THR[i] }} aria-hidden="true"><path d={ICON[i]} /></svg>
                    <div className="ih-hand px-5 text-center text-[clamp(16px,1.7vw,23px)] leading-none text-[#3b2a1c]">{it.name}</div>
                  </div>
                </div>
                <div className="ih-hand relative z-10 -mt-1.5 -rotate-6 border border-[#e5d6bf] bg-white px-2.5 py-0.5 text-[17px] text-[#8a5a32] shadow-sm">no. 0{i + 1}</div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Link to="/contact" data-testid="industries-contact-link"
            className="inline-flex items-center gap-3 rounded-full bg-[#2b1d16] px-8 py-4 text-xs tracking-[0.3em] text-[#FAF9F6] shadow-[0_10px_24px_rgba(43,29,22,.25)] transition-colors duration-500 hover:bg-[#8a5a32]">
            DON'T SEE YOURS? TALK TO US <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
